/** Career mode: climb from Sunday League to the Cup Final. Pure data + functions. */
import { seedFromString } from '../core/rng';
import type { GradeReport } from '../core/contracts';
import type { UnlockId } from './unlocks';

export type Difficulty = 'rookie' | 'pro' | 'legend';
export type TierId = 'sunday' | 'regional' | 'national' | 'continental' | 'cup_final';

export interface CareerTier {
  id: TierId;
  name: string;
  /** Used in the competition string, e.g. "Regional League — Matchday 3". */
  competition: string;
  matches: number;
  baseAudience: number;
  /** 0..1 sim drama for this tier. */
  drama: number;
  difficulty: Difficulty;
  halfLengthSeconds: number;
  /** Granted on promotion out of (or winning) this tier. */
  rewards: UnlockId[];
  blurb: string;
}

export const CAREER_TIERS: readonly CareerTier[] = [
  { id: 'sunday', name: 'Sunday League', competition: 'Sunday League', matches: 3, baseAudience: 4_000, drama: 0.35, difficulty: 'rookie', halfLengthSeconds: 180,
    rewards: ['gfx_regional'], blurb: 'Two cameras would do. You have nine. Learn the basics.' },
  { id: 'regional', name: 'Regional', competition: 'Regional League', matches: 4, baseAudience: 120_000, drama: 0.5, difficulty: 'rookie', halfLengthSeconds: 240,
    rewards: ['cam_spidercam'], blurb: 'Local TV. The booth starts asking for things.' },
  { id: 'national', name: 'National', competition: 'National Premier Division', matches: 4, baseAudience: 2_100_000, drama: 0.62, difficulty: 'pro', halfLengthSeconds: 240,
    rewards: ['gfx_national', 'cam_blimp'], blurb: 'Prime time. Millions are watching your cuts.' },
  { id: 'continental', name: 'Continental', competition: 'Continental Champions Cup', matches: 4, baseAudience: 18_000_000, drama: 0.75, difficulty: 'pro', halfLengthSeconds: 300,
    rewards: ['gfx_continental', 'cam_super_slomo'], blurb: 'Floodlit European nights. Every miss is noticed.' },
  { id: 'cup_final', name: 'Cup Final', competition: 'World Club Cup Final', matches: 1, baseAudience: 300_000_000, drama: 0.9, difficulty: 'legend', halfLengthSeconds: 360,
    rewards: ['gfx_final'], blurb: 'One match. Three hundred million people. No pressure.' },
];

/** Promotion needs an average Director Rating at or above the B threshold. */
export const PROMOTION_RATING = 70;

/**
 * Mirror of the grader's rating → letter bands (grading/grader.ts LETTER_THRESHOLDS; meta stays
 * independent of grading). Used for averages on the career screen. tests/meta.test.ts keeps them in sync.
 */
export function letterForRating(r: number): GradeReport['letter'] {
  if (r >= 92) return 'S';
  if (r >= 82) return 'A';
  if (r >= 70) return 'B';
  if (r >= 58) return 'C';
  if (r >= 45) return 'D';
  return 'F';
}

export interface CareerMatchResult {
  matchIndex: number;
  seed: number;
  rating: number;
  letter: GradeReport['letter'];
  total: number;
  peakViewers: number;
}

export interface CareerState {
  /** Random per-save salt so each career has its own fixtures. */
  careerSeed: number;
  tierIndex: number;
  /** Index of the next match to play within the tier. */
  matchIndex: number;
  /** Bumped when a tier has to be replayed (new fixtures). */
  attempt: number;
  results: CareerMatchResult[];
  completedTiers: TierId[];
  champion: boolean;
}

export function newCareer(careerSeed: number): CareerState {
  return { careerSeed: careerSeed >>> 0, tierIndex: 0, matchIndex: 0, attempt: 0, results: [], completedTiers: [], champion: false };
}

export function currentTier(state: CareerState): CareerTier {
  return CAREER_TIERS[Math.min(state.tierIndex, CAREER_TIERS.length - 1)]!;
}

export interface CareerMatchPlan {
  tier: CareerTier;
  tierIndex: number;
  matchIndex: number;
  /** 1-based for display. */
  matchNumber: number;
  seed: number;
  competition: string;
  drama: number;
  baseAudience: number;
  difficulty: Difficulty;
  halfLengthSeconds: number;
}

/** Deterministic plan for the next career match (same save → same fixture). */
export function nextCareerMatch(state: CareerState): CareerMatchPlan {
  const tier = currentTier(state);
  const matchIndex = Math.min(state.matchIndex, tier.matches - 1);
  const seed = seedFromString(`career:${state.careerSeed}:${tier.id}:${state.attempt}:${matchIndex}`);
  const competition = tier.matches > 1 ? `${tier.competition} — Matchday ${matchIndex + 1}` : tier.competition;
  return {
    tier, tierIndex: state.tierIndex, matchIndex, matchNumber: matchIndex + 1, seed, competition,
    drama: tier.drama, baseAudience: tier.baseAudience, difficulty: tier.difficulty, halfLengthSeconds: tier.halfLengthSeconds,
  };
}

export function tierAverage(results: readonly CareerMatchResult[]): number {
  if (!results.length) return 0;
  return results.reduce((s, r) => s + r.rating, 0) / results.length;
}

export type CareerOutcome = 'continue' | 'promoted' | 'retry_tier' | 'champion';

export interface CareerRecordResult {
  state: CareerState;
  outcome: CareerOutcome;
  /** Rewards earned this step (caller grants them via unlocks.grantUnlocks). */
  rewards: UnlockId[];
  average: number;
  tier: CareerTier;
}

/** Record a finished career match. Pure: returns the next state. */
export function recordCareerResult(state: CareerState, r: Omit<CareerMatchResult, 'matchIndex'>): CareerRecordResult {
  const tier = currentTier(state);
  if (state.champion) return { state, outcome: 'champion', rewards: [], average: tierAverage(state.results), tier };
  const results = [...state.results, { ...r, matchIndex: state.matchIndex }];
  const average = tierAverage(results);
  if (results.length < tier.matches) {
    return { state: { ...state, results, matchIndex: state.matchIndex + 1 }, outcome: 'continue', rewards: [], average, tier };
  }
  if (average < PROMOTION_RATING) {
    return { state: { ...state, results: [], matchIndex: 0, attempt: state.attempt + 1 }, outcome: 'retry_tier', rewards: [], average, tier };
  }
  const completedTiers = [...state.completedTiers, tier.id];
  const last = state.tierIndex >= CAREER_TIERS.length - 1;
  if (last) {
    return { state: { ...state, results, completedTiers, champion: true }, outcome: 'champion', rewards: tier.rewards, average, tier };
  }
  return {
    state: { ...state, tierIndex: state.tierIndex + 1, matchIndex: 0, attempt: 0, results: [], completedTiers },
    outcome: 'promoted', rewards: tier.rewards, average, tier,
  };
}

export function sanitizeCareer(raw: unknown, fallbackSeed: number): CareerState {
  const r = (raw && typeof raw === 'object' ? raw : null) as Partial<CareerState> | null;
  if (!r) return newCareer(fallbackSeed);
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  const tierIndex = Math.max(0, Math.min(CAREER_TIERS.length - 1, Math.floor(num(r.tierIndex, 0))));
  return {
    careerSeed: num(r.careerSeed, fallbackSeed) >>> 0,
    tierIndex,
    matchIndex: Math.max(0, Math.min(CAREER_TIERS[tierIndex]!.matches - 1, Math.floor(num(r.matchIndex, 0)))),
    attempt: Math.max(0, Math.floor(num(r.attempt, 0))),
    results: Array.isArray(r.results) ? r.results.filter((x) => x && typeof x.rating === 'number') : [],
    completedTiers: Array.isArray(r.completedTiers) ? r.completedTiers.filter((t) => CAREER_TIERS.some((c) => c.id === t)) : [],
    champion: r.champion === true,
  };
}
